import { Tile } from "./types";

/**
 * Which way a ramp or a slope climbs, for drawing it that way round. Asked
 * for on 2026-09-27: "fix stairs so they render properly when a west/east
 * ramp" and "add actual ramps which don't have steps, both north/south and
 * west/east varieties".
 *
 * The answer is the server's own rule read on this side of the wire (see
 * StepTarget in internal/game/map.go), so the drawing and the walking can
 * never disagree about which way is up. A foot climbs onto the first side,
 * of north, east, south and west in that order, that is rock on its own
 * level with ground over it on the level above; a head comes down off the
 * far end of the foot under it, so it climbs the way its foot does.
 *
 * What comes back is the direction uphill as an index — 0 north, 1 east,
 * 2 south, 3 west — which is the variant the tile is drawn with and the
 * number of quarter turns sprites.ts turns the drawing by. A tile with no
 * mountain beside it at all, which the map never makes, is drawn climbing
 * north rather than not at all.
 */
export function rampDir(g: LevelSource, t: Tile, x: number, y: number): number {
  let z: number | undefined;
  for (let zz = -8; zz <= 8 && z === undefined; zz++) if (g.tileOn(zz, x, y) === t) z = zz;
  if (z === undefined) return 0;
  return rampDirOn(g, z, x, y);
}

/**
 * rampDir for a ramp or slope whose level is already known: the foot or
 * head standing on (x, y) of level z. The oblique view asks it of the
 * levels it is drawing (see inclineAt in oblique.ts), where the level is
 * the question rather than something to go looking for.
 */
export function rampDirOn(g: LevelSource, z: number, x: number, y: number): number {
  if (isRampFoot(g.tileOn(z, x, y))) return footClimbs(g, z, x, y) ?? 0;
  // A head lets you down off the far end of the foot under it, in line
  // with the slope (2026-10-08), so it climbs the way its foot does —
  // unless that end is shut, when it is the first side that lets down.
  const up = footClimbs(g, z - 1, x, y);
  if (up !== undefined) {
    const [dx, dy] = SIDES[(up + 2) & 3];
    if (letsDown(g, z, x + dx, y + dy)) return up;
  }
  for (let i = 0; i < 4; i++) {
    const [dx, dy] = SIDES[i];
    if (letsDown(g, z, x + dx, y + dy)) return (i + 2) & 3;
  }
  return 0;
}

/** The way the foot of a ramp or slope at (x, y) of level z climbs, if it is one and climbs anywhere. */
function footClimbs(g: LevelSource, z: number, x: number, y: number): number | undefined {
  if (!isRampFoot(g.tileOn(z, x, y))) return undefined;
  for (let i = 0; i < 4; i++) {
    const [dx, dy] = SIDES[i];
    if (g.tileOn(z, x + dx, y + dy) === Tile.Rock && walkable(g.tileOn(z + 1, x + dx, y + dy))) return i;
  }
  return undefined;
}

/** Whether a head on level z can let a walker down onto (x, y): nothing on its level, ground below. */
function letsDown(g: LevelSource, z: number, x: number, y: number): boolean {
  return g.tileOn(z, x, y) === undefined && walkable(g.tileOn(z - 1, x, y));
}

/** Whether a tile is one of the four that climb or come down a mountain. */
export function isRampTile(t: Tile): boolean {
  return t === Tile.Ramp || t === Tile.RampTop || t === Tile.Slope || t === Tile.SlopeTop;
}

/** Whether a tile is the foot of a ramp or a slope, on the lower of its two levels. */
export function isRampFoot(t: number | undefined): boolean {
  return t === Tile.Ramp || t === Tile.Slope;
}

/** Whether a tile is the head of a ramp or a slope, on the level above its foot. */
export function isRampHead(t: number | undefined): boolean {
  return t === Tile.RampTop || t === Tile.SlopeTop;
}

/** The levels of the world, as much of them as rampDir asks about. */
export interface LevelSource {
  tileOn(z: number, x: number, y: number): number | undefined;
}

/** North, east, south, west: the order the server tries the sides in. */
const SIDES: [number, number][] = [[0, -1], [1, 0], [0, 1], [-1, 0]];

/** The server's Walkable, for the two questions asked here. */
function walkable(t: number | undefined): boolean {
  return t !== undefined && !NOT_WALKABLE.has(t);
}

const NOT_WALKABLE = new Set<number>([
  Tile.Water, Tile.Wall, Tile.Tree, Tile.Rock, Tile.CaveWall, Tile.Palisade,
  Tile.Hedge, Tile.Hillside, Tile.HobbitDoor, Tile.MillWheel, Tile.PartyTree, Tile.Ship,
  Tile.FrozenWaste,
]);
